import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Add as AddIcon,
  TextFields as TextFieldsIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { textbausteineApi, mandantenApi, gruppenApi } from '../services/api';
import type { Textbaustein, TextbausteinContent, Mandant, Gruppe } from '../types/api';
import RichTextEditor from '../components/RichTextEditor';

const Textbausteine: React.FC = () => {
  const [textbausteine, setTextbausteine] = useState<Textbaustein[]>([]);
  const [kategorien, setKategorien] = useState<string[]>([]);
  const [mandanten, setMandanten] = useState<Mandant[]>([]);
  const [gruppen, setGruppen] = useState<Gruppe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKategorie, setSelectedKategorie] = useState<string>('Alle');
  const [selectedBaustein, setSelectedBaustein] =
    useState<TextbausteinContent | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editMetadata, setEditMetadata] = useState<Textbaustein | null>(null);
  const [saving, setSaving] = useState(false);

  // Für neuen Textbaustein
  const [newBaustein, setNewBaustein] = useState({
    bausteinName: '',
    kategorie: '',
    kurzerText: '',
    tags: '',
    content: '',
    kiVerbessern: false,
    mandantID: undefined as number | undefined,
    gruppeID: undefined as number | undefined,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Lade Textbausteine und Kategorien (erforderlich)
      const [bausteineResponse, kategorienResponse] = await Promise.all([
        textbausteineApi.getAll(),
        textbausteineApi.getKategorien(),
      ]);
      setTextbausteine(bausteineResponse.data);
      setKategorien(['Alle', ...kategorienResponse.data]);

      // Lade Mandanten und Gruppen (optional, nur für Admins)
      try {
        const mandantenResponse = await mandantenApi.getAll();
        setMandanten(mandantenResponse.data);
      } catch (err: any) {
        console.log('Mandanten konnten nicht geladen werden (benötigt Admin-Rechte)');
        setMandanten([]);
      }

      try {
        const gruppenResponse = await gruppenApi.getAll();
        setGruppen(gruppenResponse.data);
      } catch (err: any) {
        console.log('Gruppen konnten nicht geladen werden');
        setGruppen([]);
      }
    } catch (err: any) {
      console.error('Textbausteine-Fehler:', err);
      setError(
        err.response?.data?.message || 'Fehler beim Laden der Textbausteine'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewBaustein = async (bausteinId: number) => {
    try {
      const response = await textbausteineApi.getContent(bausteinId);
      setSelectedBaustein(response.data);
      setViewDialogOpen(true);
    } catch (err: any) {
      alert('Fehler beim Laden des Textbaustein-Inhalts');
    }
  };

  const handleEditBaustein = async (bausteinId: number) => {
    try {
      const [contentResponse, metadataResponse] = await Promise.all([
        textbausteineApi.getContent(bausteinId),
        textbausteineApi.getById(bausteinId),
      ]);
      setSelectedBaustein(contentResponse.data);
      setEditContent(contentResponse.data.content || '');
      setEditMetadata(metadataResponse.data);
      setEditDialogOpen(true);
    } catch (err: any) {
      alert('Fehler beim Laden des Textbaustein-Inhalts');
    }
  };

  const handleSaveContent = async () => {
    if (!selectedBaustein || !editMetadata) return;

    try {
      setSaving(true);
      // Speichere Metadaten zuerst, dann Content (sequenziell statt parallel)
      // um Race Conditions zu vermeiden
      const updateResponse = await textbausteineApi.update(selectedBaustein.bausteinID, {
        bausteinName: editMetadata.bausteinName,
        kategorie: editMetadata.kategorie,
        kurzerText: editMetadata.kurzerText,
        tags: undefined,
        kiVerbessern: !!editMetadata.kiVerbessern,
      });

      await textbausteineApi.updateContent(selectedBaustein.bausteinID, {
        content: editContent,
      });

      // Aktualisiere die lokalen Daten mit der Server-Antwort
      console.log('Updated textbaustein from server:', updateResponse.data);

      alert('Textbaustein erfolgreich gespeichert!');
      setEditDialogOpen(false);
      setSelectedBaustein(null);
      setEditMetadata(null);
      loadData(); // Reload to show updated data
    } catch (err: any) {
      alert('Fehler beim Speichern: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBaustein = async () => {
    if (!newBaustein.bausteinName || !newBaustein.kategorie) {
      alert('Bitte Name und Kategorie eingeben!');
      return;
    }

    try {
      setSaving(true);
      // Erst den Textbaustein erstellen
      const response = await textbausteineApi.create({
        bausteinName: newBaustein.bausteinName,
        kategorie: newBaustein.kategorie,
        kurzerText: newBaustein.kurzerText || undefined,
        tags: newBaustein.tags || undefined,
        kiVerbessern: newBaustein.kiVerbessern,
        mandantID: newBaustein.mandantID,
        gruppeID: newBaustein.gruppeID,
      });

      // Dann den Inhalt speichern, falls vorhanden
      if (newBaustein.content) {
        await textbausteineApi.updateContent(response.data.bausteinID, {
          content: newBaustein.content,
        });
      }

      alert('Textbaustein erfolgreich erstellt!');
      setCreateDialogOpen(false);
      setNewBaustein({
        bausteinName: '',
        kategorie: '',
        kurzerText: '',
        tags: '',
        content: '',
        kiVerbessern: false,
        mandantID: undefined,
        gruppeID: undefined,
      });
      loadData();
    } catch (err: any) {
      alert('Fehler beim Erstellen: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bausteinId: number) => {
    if (!window.confirm('Textbaustein wirklich löschen?')) return;

    try {
      await textbausteineApi.delete(bausteinId);
      setTextbausteine(textbausteine.filter((t) => t.bausteinID !== bausteinId));
    } catch (err: any) {
      alert('Fehler beim Löschen des Textbausteins');
    }
  };

  const filteredTextbausteine = textbausteine.filter((baustein) => {
    const matchesKategorie =
      selectedKategorie === 'Alle' || baustein.kategorie === selectedKategorie;
    const matchesSearch =
      baustein.bausteinName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (baustein.kurzerText || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesKategorie && matchesSearch;
  });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Textbausteine</Typography>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Neuer Textbaustein">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Neu
            </Button>
          </Tooltip>
          <Tooltip title="Aktualisieren">
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Kategorien-Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedKategorie}
          onChange={(_, newValue) => setSelectedKategorie(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {kategorien.map((kategorie) => (
            <Tab key={kategorie} label={kategorie} value={kategorie} />
          ))}
        </Tabs>
      </Paper>

      {/* Textbausteine Grid */}
      {filteredTextbausteine.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Keine Textbausteine vorhanden
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr',
            },
            gap: 2,
          }}
        >
          {filteredTextbausteine.map((baustein) => (
            <Card key={baustein.bausteinID}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <TextFieldsIcon color="primary" />
                  <Typography variant="h6">{baustein.bausteinName}</Typography>
                </Box>
                <Chip
                  label={baustein.kategorie}
                  size="small"
                  color="primary"
                  sx={{ mb: 1 }}
                />
                {baustein.kurzerText && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      mt: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {baustein.kurzerText}
                  </Typography>
                )}
                <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                  Erstellt: {new Date(baustein.erstelltAm).toLocaleDateString('de-DE')}
                </Typography>
              </CardContent>
              <CardActions>
                <Tooltip title="Anzeigen">
                  <IconButton
                    size="small"
                    onClick={() => handleViewBaustein(baustein.bausteinID)}
                  >
                    <ViewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Bearbeiten">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleEditBaustein(baustein.bausteinID)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Löschen">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(baustein.bausteinID)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Dialog zum Anzeigen des Textbausteins */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedBaustein && (
          <>
            <DialogTitle>{selectedBaustein.bausteinName}</DialogTitle>
            <DialogContent dividers>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Kategorie:
                </Typography>
                <Chip label={selectedBaustein.kategorie} size="small" />
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Dateipfad:
                </Typography>
                <Typography variant="body2">
                  {selectedBaustein.dateiPfad}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary" mb={1}>
                  Inhalt:
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', maxHeight: '400px', overflow: 'auto' }}>
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: selectedBaustein.content || '<p>Kein Inhalt verfügbar</p>',
                    }}
                    sx={{
                      '& h1': { fontSize: '2em', fontWeight: 'bold', margin: '0.67em 0' },
                      '& h2': { fontSize: '1.5em', fontWeight: 'bold', margin: '0.75em 0' },
                      '& h3': { fontSize: '1.17em', fontWeight: 'bold', margin: '0.83em 0' },
                      '& ul, & ol': { paddingLeft: '1.5em', margin: '0.5em 0' },
                      '& blockquote': {
                        borderLeft: '3px solid #ccc',
                        paddingLeft: '1em',
                        marginLeft: 0,
                        fontStyle: 'italic',
                      },
                      '& code': {
                        backgroundColor: '#f4f4f4',
                        padding: '0.2em 0.4em',
                        borderRadius: '3px',
                        fontFamily: 'monospace',
                      },
                    }}
                  />
                </Paper>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)}>Schließen</Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (selectedBaustein.content) {
                    navigator.clipboard.writeText(selectedBaustein.content);
                    alert('In Zwischenablage kopiert!');
                  }
                }}
              >
                Kopieren
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog zum Bearbeiten des Textbausteins */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !saving && setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedBaustein && editMetadata && (
          <>
            <DialogTitle>
              Textbaustein bearbeiten: {selectedBaustein.bausteinName}
            </DialogTitle>
            <DialogContent dividers>
              <Box mb={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editMetadata.kiVerbessern || false}
                      onChange={(e) =>
                        setEditMetadata({ ...editMetadata, kiVerbessern: e.target.checked })
                      }
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <AIIcon fontSize="small" color={editMetadata.kiVerbessern ? 'primary' : 'action'} />
                      <Typography variant="body2">
                        Textbaustein automatisch mit KI verbessern beim Einfügen
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              <RichTextEditor
                content={editContent}
                onChange={setEditContent}
                minHeight="400px"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
                Abbrechen
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveContent}
                disabled={saving}
              >
                {saving ? 'Speichere...' : 'Speichern'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog zum Erstellen eines neuen Textbausteins */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !saving && setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Neuer Textbaustein</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              label="Name"
              value={newBaustein.bausteinName}
              onChange={(e) =>
                setNewBaustein({ ...newBaustein, bausteinName: e.target.value })
              }
              required
            />
            <TextField
              fullWidth
              label="Kategorie"
              value={newBaustein.kategorie}
              onChange={(e) =>
                setNewBaustein({ ...newBaustein, kategorie: e.target.value })
              }
              required
            />
            <TextField
              fullWidth
              label="Kurzer Text"
              value={newBaustein.kurzerText}
              onChange={(e) =>
                setNewBaustein({ ...newBaustein, kurzerText: e.target.value })
              }
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="Tags (optional)"
              value={newBaustein.tags}
              onChange={(e) =>
                setNewBaustein({ ...newBaustein, tags: e.target.value })
              }
            />
            <FormControl fullWidth>
              <InputLabel>Mandant (optional)</InputLabel>
              <Select
                value={newBaustein.mandantID || ''}
                onChange={(e) =>
                  setNewBaustein({
                    ...newBaustein,
                    mandantID: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
                label="Mandant (optional)"
              >
                <MenuItem value="">
                  <em>Kein Mandant</em>
                </MenuItem>
                {mandanten.map((mandant) => (
                  <MenuItem key={mandant.mandantID} value={mandant.mandantID}>
                    {mandant.mandantName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Gruppe (optional)</InputLabel>
              <Select
                value={newBaustein.gruppeID || ''}
                onChange={(e) =>
                  setNewBaustein({
                    ...newBaustein,
                    gruppeID: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
                label="Gruppe (optional)"
              >
                <MenuItem value="">
                  <em>Keine Gruppe</em>
                </MenuItem>
                {gruppen.map((gruppe) => (
                  <MenuItem key={gruppe.gruppeID} value={gruppe.gruppeID}>
                    {gruppe.gruppenName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newBaustein.kiVerbessern}
                  onChange={(e) =>
                    setNewBaustein({ ...newBaustein, kiVerbessern: e.target.checked })
                  }
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <AIIcon fontSize="small" color={newBaustein.kiVerbessern ? 'primary' : 'action'} />
                  <Typography variant="body2">
                    Textbaustein automatisch mit KI verbessern beim Einfügen
                  </Typography>
                </Box>
              }
            />
            <Box>
              <Typography variant="subtitle2" mb={1}>
                Inhalt:
              </Typography>
              <RichTextEditor
                content={newBaustein.content}
                onChange={(html) =>
                  setNewBaustein({ ...newBaustein, content: html })
                }
                minHeight="300px"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleCreateBaustein}
            disabled={saving}
          >
            {saving ? 'Erstelle...' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Textbausteine;
